/** @jsx isolateComponent */

import React from 'react';
import { FormLabel } from '../../forms/FormLabel';
import { isolateComponent } from '@zapier/style-encapsulation';
import { BooleanInput, BooleanInputType } from './BooleanInput';

export type Props = {
  /**
   * Optional accessibility label for the underlying `input`.
   * Using a `label` tag is generally preferred for more clickable area.
   */
  ariaLabel?: string;

  /**
   * Indicates whether the input is checked.
   */
  checked: boolean;

  /**
   * The content to render for the label.
   */
  children: React.ReactNode;

  /**
   * `id` that is attached to the underlying `input`, used to attach
   * an external `label` to it via the `for` attribute.
   */
  id?: string;

  /**
   * Indicates whether the input is disabled.
   */
  isDisabled?: boolean;

  /**
   * Indicates whether the input is errored.
   */
  isErrored?: boolean;

  /**
   * Indicates whether the input is required.
   */
  isRequired?: boolean;

  /**
   * `name` that is attached to the underlying `input`, used to group
   * multiple `BooleanInput` values together.
   */
  name?: string;

  /**
   * Handler for when the `input` changes between checked and unchecked.
   */
  onChange: (e: React.SyntheticEvent) => void;

  /**
   * The `tabIndex`. The default is almost always correct unless this
   * `LabeledBooleanInput` is being used inside a larger control that manages
   * focus state and keyboard interactions. e.g. a dropdown.
   */
  tabIndex: number;

  /**
   * The type of `input` to render.
   */
  type: BooleanInputType;

  /**
   * `value` that is attached to the underlying `input`, used to differentiate
   * one `input` from another.
   */
  value?: string;
};

export const LabeledBooleanInput = (props: Props) => {
  return (
    <FormLabel
      isDisabled={props.isDisabled}
      isErrored={props.isErrored}
      isRequired={props.isRequired}
      isSelected={props.checked}
      layout="auto-content"
    >
      <BooleanInput
        ariaLabel={props.ariaLabel}
        checked={props.checked}
        disabled={props.isDisabled}
        id={props.id}
        isErrored={props.isErrored}
        name={props.name}
        onChange={props.onChange}
        tabIndex={props.tabIndex}
        type={props.type}
        value={props.value}
      />
      {props.children}
    </FormLabel>
  );
};

LabeledBooleanInput.defaultProps = {
  checked: false,
  onChange: () => {},
  tabIndex: 0,
};
